class Geolocation {
  location = JSON.parse(WYATT.shopify.cart?.attributes?.location || "{}");

  sortedStores = null;

  constructor() {
    if (!this.location?.coordinates) {
      this.updateLocationFromUser();
    } else {
      WYATT.PubSub.publish(WYATT.PubSub.events.userLocationSet);
      document.documentElement.dataset.wyattGeolocationStatus = "location-set";
    }

    this.sortLocations();

    WYATT.PubSub.subscribe(
      WYATT.PubSub.events.userLocationSet,
      this.sortLocations.bind(this),
    );
  }

  setLocation(location) {
    this.location = location;

    WYATT.PubSub.publish(WYATT.PubSub.events.userLocationSet);
    document.documentElement.dataset.wyattGeolocationStatus = "location-set";

    this.setLocationOnCart();
  }

  async updateLocationFromUser() {
    const coordinates = await this.gettingUserLocation();

    if (!coordinates) {
      return;
    }

    const response = await fetch(
      `${WYATT.config.mapbox.endpoints.reverse}?${[
        `longitude=${coordinates.lng}`,
        `latitude=${coordinates.lat}`,
        `access_token=${WYATT.config.mapbox.token}`,
        `limit=1`,
        `types=postcode`,
      ].join("&")}`,
    );

    const data = await response.json();
    const zip = data.features[0].properties.context.postcode.name;

    this.setLocation({ coordinates, zip });
  }

  async updateLocationFromZip(zip) {
    const response = await fetch(`
      ${WYATT.config.mapbox.endpoints.forward}?${[
        `q=${zip}`,
        `country=US`,
        `proximity=${WYATT.config.mapbox.defaultProximity.lng},${WYATT.config.mapbox.defaultProximity.lat}`,
        `access_token=${WYATT.config.mapbox.token}`,
        `limit=1`,
      ].join("&")}
    `);
    const data = await response.json();

    const coordinates = {
      lat: data.features[0].properties.coordinates.latitude,
      lng: data.features[0].properties.coordinates.longitude,
    };

    this.setLocation({ coordinates, zip });
    this.setLocationOnCart();
  }

  gettingUserLocation() {
    if (!window.navigator.geolocation) {
      document.documentElement.dataset.wyattGeolocationStatus =
        "location-not-supported";
      return false;
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;

          resolve({
            lat: latitude,
            lng: longitude,
          });
        },
        (error) => {
          switch (error.code) {
            case 1:
              document.documentElement.dataset.wyattGeolocationStatus =
                "location-denied";
              break;
            case 2:
              document.documentElement.dataset.wyattGeolocationStatus =
                "location-error";
              break;
            default:
              document.documentElement.dataset.wyattGeolocationStatus =
                "location-timeout";
              break;
          }

          resolve(false);
        },
        {
          enableHighAccuracy: true,
        },
      );
    });
  }

  sortLocations() {
    const { stores } = WYATT.shopify;

    if (!this.location.coordinates || !Array.isArray(stores)) {
      return [];
    }

    const sortedStores = stores.toSorted((locationA, locationB) => {
      const distanceA = this.getDistanceBetween(
        locationA.coordinates,
        this.location.coordinates,
      );
      const distanceB = this.getDistanceBetween(
        locationB.coordinates,
        this.location.coordinates,
      );

      return distanceA - distanceB;
    });

    this.sortedStores = sortedStores;
    WYATT.PubSub.publish(WYATT.PubSub.events.storesSorted);

    return sortedStores;
  }

  getDistanceBetween(locationA, locationB) {
    if (!locationA || !locationB) {
      return 0;
    }

    const degreesToRadians = (degrees) => (degrees * Math.PI) / 180;

    const radiusOfEarthInMeters = 6371000;
    const φ1 = degreesToRadians(locationA.lat);
    const φ2 = degreesToRadians(locationB.lat);
    const Δφ = degreesToRadians(locationB.lat - locationA.lat);
    const Δλ = degreesToRadians(locationB.lng - locationA.lng);

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return radiusOfEarthInMeters * c;
  }

  getStoresWithinRadius(meters, storeTypes = []) {
    let stores = this.sortedStores;

    if (!this.location.coordinates || !stores) {
      return [];
    }

    if (storeTypes.length > 0) {
      stores = stores.filter((store) => storeTypes.includes(store.store_type));
    }

    return stores.filter((store) => {
      if (!store.coordinates) {
        return false;
      }

      const distance = this.getDistanceBetween(
        store.coordinates,
        this.location.coordinates,
      );

      return distance <= meters;
    });
  }

  async setLocationOnCart() {
    await WYATT.Cart.update({
      attributes: {
        location: JSON.stringify(this.location),
      },
    });
  }
}

WYATT.Geolocation = new Geolocation();

export default Geolocation;
